import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserCard from "./UserCard";
import ProfileCard from "./ProfileCard";
import AvatarUploader from "./AvatarUploader";
import "../../css/components/Cabinet.css";
import {usersApi, notificationsApi} from "../../api";
import CreateProfilePopup from "./CreateProfilePopup";
import MyBattlePersons from "./MyBattlePersons";
import HrnbButton from "../controls/HrnbButton";
import {HrnbTab, HrnbTabs} from "../controls/HrnbTabs";
import HrnbForm from "../controls/HrnbForm";
import HrnbFormControl from "../controls/HrnbFormControl";
import HrnbInput from "../controls/HrnbInput";
import HrnbAlert from "../controls/HrnbAlert";
import {CreateProfileIcon, SaveIcon} from "../../icons";

const NOTIFICATION_TYPES = [
    { value: 'BATTLE_STARTED', label: 'Начало баттла' },
    { value: 'ROUND_STARTED', label: 'Начало раунда' },
    { value: 'ROUND_ENDED', label: 'Окончание раунда' },
    { value: 'FORUM_COMMENT', label: 'Комментарий на форуме' },
    { value: 'CHAT_MESSAGE', label: 'Сообщение в чате' },
    { value: 'BATTLE_INVITE', label: 'Приглашение в баттл' },
    { value: 'BATTLE_APPLICATION', label: 'Заявка в баттл' },
    { value: 'BATTLE_PUBLISHED', label: 'Публикация баттла' },
    { value: 'APPLICATION_APPROVED', label: 'Заявка одобрена' },
    { value: 'APPLICATION_REJECTED', label: 'Заявка отклонена' },
    { value: 'NEW_FOLLOWER', label: 'Новый подписчик' },
    { value: 'EVENT_REMINDER', label: 'Напоминание о событии' },
    { value: 'VOTING_STARTED', label: 'Начало голосования' },
    { value: 'TRACK_DISQUALIFIED', label: 'Трек дисквалифицирован' },
    { value: 'SYSTEM', label: 'Системные уведомления' },
    { value: 'DUEL_INVITE', label: 'Приглашение в дуэль' },
    { value: 'DUEL_STARTED', label: 'Начало дуэли' },
    { value: 'DUEL_ROUND_STARTED', label: 'Начало раунда дуэли' },
    { value: 'DUEL_TRACK_SUBMITTED', label: 'Трек отправлен в дуэли' },
    { value: 'DUEL_ROUND_ENDED', label: 'Окончание раунда дуэли' },
    { value: 'DUEL_FINISHED', label: 'Завершение дуэли' },
    { value: 'DUEL_QUICK_MATCH_FOUND', label: 'Быстрый матч найден' },
    { value: 'DUEL_JUDGE_INVITE', label: 'Приглашение судьёй' },
    { value: 'DUEL_STATUS_CHANGED', label: 'Статус дуэли изменён' },
    { value: 'DUEL_CANCELLED', label: 'Дуэль отменена' },
    { value: 'DUEL_PARTICIPANT_KICKED', label: 'Исключение из дуэли' },
    { value: 'DUEL_APPEAL_SUBMITTED', label: 'Апелляция подана' },
    { value: 'DUEL_APPEAL_REVIEWED', label: 'Апелляция рассмотрена' },
    { value: 'DUEL_CHAT_MESSAGE', label: 'Сообщение в чате дуэли' },
];

const Cabinet = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState("personal-info");

    // State для смены пароля
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(null);
    const [passwordError, setPasswordError] = useState(null);

    // State для настроек уведомлений (каналы)
    const [channelSettings, setChannelSettings] = useState({
        emailNotificationsEnabled: true,
        pushNotificationsEnabled: true,
        inAppNotificationsEnabled: true
    });
    const [notificationPreferences, setNotificationPreferences] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [notificationsSaving, setNotificationsSaving] = useState(false);
    const [notificationsSuccess, setNotificationsSuccess] = useState(null);
    const [notificationsError, setNotificationsError] = useState(null);

    useEffect(() => {
        let mounted = true;

        const fetchUser = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await usersApi.current();
                // Поддерживаем оба варианта: { data: { ... } } и сразу data
                const data = res && (res.data ?? res);

                if (!data) {
                    throw new Error("Нет данных пользователя");
                }

                if (!mounted) return;

                setCurrentUser(data);

                const firstProfile =
                    Array.isArray(data.profiles) && data.profiles.length > 0
                        ? data.profiles[0]
                        : null;
                setProfile(firstProfile);
            } catch (err) {
                if (!mounted) return;
                setError("Ошибка загрузки пользователя");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchUser();

        // Загрузка настроек уведомлений
        const fetchNotificationSettings = async () => {
            try {
                const [settings, preferences] = await Promise.all([
                    usersApi.getSettings(),
                    notificationsApi.getPreferences()
                ]);
                if (!mounted) return;
                setChannelSettings(settings);
                setNotificationPreferences(Array.isArray(preferences) ? preferences : []);
            } catch (err) {
                console.error('Failed to load notification settings:', err);
            } finally {
                if (mounted) setNotificationsLoading(false);
            }
        };
        fetchNotificationSettings();

        return () => {
            mounted = false;
        };
    }, []);

    // Обработчики для смены пароля
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitPassword = async (e) => {
        e.preventDefault();

        if (!passwordForm.currentPassword || !passwordForm.newPassword) {
            setPasswordError('Заполните все поля');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            setPasswordError('Пароль должен содержать минимум 6 символов');
            return;
        }

        setPasswordLoading(true);
        setPasswordError(null);
        setPasswordSuccess(null);

        try {
            // В будущем: интеграция с API смены пароля
            await new Promise(resolve => setTimeout(resolve, 1000)); // Имитация

            setPasswordSuccess('Пароль успешно изменён!');
            setPasswordForm({ currentPassword: '', newPassword: '' });
        } catch (err) {
            setPasswordError(err.message || 'Ошибка при смене пароля');
        } finally {
            setPasswordLoading(false);
        }
    };

    // Обработчики для настроек уведомлений (каналы)
    const handleChannelToggle = (key) => {
        setChannelSettings(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Гранулярное переключение типа уведомления для канала
    const handleTypeToggle = (channel, type) => {
        setNotificationPreferences(prev => {
            const pref = prev.find(p => p.channel === channel);
            if (!pref) return prev;

            const isEnabled = (pref.enabledTypes || []).includes(type);
            const newTypes = isEnabled
                ? (pref.enabledTypes || []).filter(t => t !== type)
                : [...(pref.enabledTypes || []), type];

            return prev.map(p =>
                p.channel === channel ? { ...p, enabledTypes: newTypes } : p
            );
        });
    };

    const handleSaveNotifications = async () => {
        setNotificationsSaving(true);
        setNotificationsSuccess(null);
        setNotificationsError(null);

        try {
            // 1. Сохраняем глобальные настройки каналов
            await usersApi.updateSettings(channelSettings);

            // 2. Сохраняем гранулярные предпочтения
            const updatePromises = notificationPreferences.map(pref =>
                notificationsApi.updatePreferences({
                    channel: pref.channel,
                    enabledTypes: pref.enabledTypes || []
                })
            );
            await Promise.all(updatePromises);

            setNotificationsSuccess('Настройки уведомлений сохранены!');
        } catch (err) {
            console.error('Ошибка при сохранении настроек:', err);
            setNotificationsError(
                err.response?.data?.message || 'Ошибка при сохранении настроек уведомлений'
            );
        } finally {
            setNotificationsSaving(false);
        }
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div>Ошибка: {error}</div>;
    if (!currentUser) return <div>Пользователь не найден</div>;

    const openPopup = () => {
        setIsOpen(true);
    };

    const closePopup = () => {
        setIsOpen(false);
        navigate("/cabinet");
    };

    const updateProfile = (newProfile) => {
        setProfile(newProfile);
    };

    return (
        <div className="cabinet-container">
            <div className="cabinet-row">
                <div className="avatarside">
                    {profile && <AvatarUploader profileId={profile.id}/>}
                </div>
                <div className="tabs-content">
                    <div className="tabs">
                        <HrnbTabs value={activeTab}
                                  onChange={setActiveTab}
                                  className="tabs.header">
                            <HrnbTab
                                label="Личная информация"
                                value="personal-info"/>
                            <HrnbTab
                                label="Публичный профиль"
                                value="public-profile"/>
                            <HrnbTab
                                label="Мои баттлы"
                                value="my-battles"/>
                            <HrnbTab
                                label="Настройки"
                                value="settings"/>
                        </HrnbTabs>
                        <div className="tabs.body">
                            {activeTab === "personal-info" && (
                                <div className="tab-pane">
                                    <UserCard user={currentUser}/>
                                </div>
                            )}
                            {activeTab === "public-profile" && (
                                <div className="tab-pane">
                                    {profile ? (
                                        <ProfileCard profile={profile}/>
                                    ) : (
                                        <>
                                            <p>У вас пока нет публичного профиля.</p>
                                            <HrnbButton onClick={openPopup} icon={<CreateProfileIcon />}>
                                                Создать профиль
                                            </HrnbButton>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTab === "my-battles" && (
                                <div className="tab-pane">
                                    <MyBattlePersons publicProfileId={profile?.id || null} />
                                </div>
                            )}
                            {activeTab === "settings" && (
                                <div className="tab-pane">
                                    <div className="settings-section">
                                        <h3>Смена пароля</h3>
                                        {passwordSuccess && (
                                            <HrnbAlert variant="success" onClose={() => setPasswordSuccess(null)}>
                                                {passwordSuccess}
                                            </HrnbAlert>
                                        )}
                                        {passwordError && (
                                            <HrnbAlert variant="danger" onClose={() => setPasswordError(null)}>
                                                {passwordError}
                                            </HrnbAlert>
                                        )}
                                        <HrnbForm onSubmit={handleSubmitPassword}>
                                            <HrnbFormControl>
                                                <HrnbInput
                                                    type="password"
                                                    name="currentPassword"
                                                    placeholder="Текущий пароль"
                                                    value={passwordForm.currentPassword}
                                                    onChange={handlePasswordChange}
                                                    required
                                                />
                                            </HrnbFormControl>
                                            <HrnbFormControl>
                                                <HrnbInput
                                                    type="password"
                                                    name="newPassword"
                                                    placeholder="Новый пароль"
                                                    value={passwordForm.newPassword}
                                                    onChange={handlePasswordChange}
                                                    required
                                                />
                                            </HrnbFormControl>

                                            <HrnbFormControl>
                                                <HrnbButton
                                                    type="submit"
                                                    disabled={passwordLoading}
                                                >
                                                    {passwordLoading ? 'Изменение...' : 'Сменить пароль'}
                                                </HrnbButton>
                                            </HrnbFormControl>
                                        </HrnbForm>
                                    </div>

                                    <div className="settings-section">
                                        <h3>Уведомления</h3>
                                        {notificationsSuccess && (
                                            <HrnbAlert variant="success" onClose={() => setNotificationsSuccess(null)}>
                                                {notificationsSuccess}
                                            </HrnbAlert>
                                        )}
                                        {notificationsError && (
                                            <HrnbAlert variant="danger" onClose={() => setNotificationsError(null)}>
                                                {notificationsError}
                                            </HrnbAlert>
                                        )}
                                        {notificationsLoading ? (
                                            <p>Загрузка настроек...</p>
                                        ) : (
                                            <>
                                                {/* Каналы уведомлений (глобальные переключатели) */}
                                                <div className="settings-notifications">
                                                    <label className="settings-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={channelSettings.emailNotificationsEnabled}
                                                            onChange={() => handleChannelToggle('emailNotificationsEnabled')}
                                                        />
                                                        <span>Email уведомления</span>
                                                    </label>
                                                    <label className="settings-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={channelSettings.pushNotificationsEnabled}
                                                            onChange={() => handleChannelToggle('pushNotificationsEnabled')}
                                                        />
                                                        <span>Push уведомления</span>
                                                    </label>
                                                    <label className="settings-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={channelSettings.inAppNotificationsEnabled}
                                                            onChange={() => handleChannelToggle('inAppNotificationsEnabled')}
                                                        />
                                                        <span>Уведомления в приложении</span>
                                                    </label>
                                                </div>

                                                {/* Гранулярные настройки по типам уведомлений */}
                                                {notificationPreferences.length > 0 && (
                                                    <div className="settings-notification-types">
                                                        {notificationPreferences.map(pref => {
                                                            const channelLabel = {
                                                                EMAIL: 'Email',
                                                                PUSH: 'Push',
                                                                IN_APP: 'В приложении'
                                                            }[pref.channel] || pref.channel;
                                                            const channelEnabled = pref.channel === 'EMAIL'
                                                                ? channelSettings.emailNotificationsEnabled
                                                                : pref.channel === 'PUSH'
                                                                    ? channelSettings.pushNotificationsEnabled
                                                                    : channelSettings.inAppNotificationsEnabled;
                                                            return (
                                                                <details key={pref.channel} className="settings-notification-group" open={!channelEnabled ? undefined : undefined}>
                                                                    <summary className="settings-notification-group__title">
                                                                        {channelLabel} — детальные настройки
                                                                    </summary>
                                                                    <div className="settings-notification-group__types">
                                                                        {(pref.enabledTypes || []).length === 0 && channelEnabled && (
                                                                            <p className="settings-notification-group__empty">
                                                                                Все типы уведомлений отключены для этого канала
                                                                            </p>
                                                                        )}
                                                                        {NOTIFICATION_TYPES.map(type => (
                                                                            <label key={type.value} className="settings-checkbox settings-checkbox--inline">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={(pref.enabledTypes || []).includes(type.value)}
                                                                                    onChange={() => handleTypeToggle(pref.channel, type.value)}
                                                                                />
                                                                                <span>{type.label}</span>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </details>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <HrnbButton
                                            onClick={handleSaveNotifications}
                                            disabled={notificationsSaving || notificationsLoading}
                                            className="settings-save-btn"
                                            icon={<SaveIcon/>}
                                        >
                                            {notificationsSaving ? 'Сохранение...' : 'Сохранить настройки'}
                                        </HrnbButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <CreateProfilePopup show={isOpen} onHide={closePopup} onProfileCreated={updateProfile}/>
        </div>
    );
};

export default Cabinet;